import { formatVisitorCount, readVisitorCount } from './lib/visitor-counter.js';

const GUESTBOOK_ENDPOINT = '/api/guestbook';

export function initializeSiteCommunity() {
  if (document.querySelector('[data-site-community]')) return;

  const community = document.createElement('section');
  community.className = 'site-community';
  community.dataset.siteCommunity = '';
  community.setAttribute('aria-label', 'Little Pink Men community');

  const counter = document.createElement('p');
  counter.className = 'visitor-counter';
  counter.append('YOU ARE VISITOR NUMBER ');
  const count = document.createElement('span');
  count.dataset.visitorCount = '';
  count.setAttribute('aria-live', 'polite');
  count.textContent = '------';
  counter.append(count);

  const guestbookButton = document.createElement('button');
  guestbookButton.className = 'retro-button guestbook-open';
  guestbookButton.type = 'button';
  guestbookButton.textContent = 'Sign the guestbook';
  community.append(counter, guestbookButton);

  const ticker = document.querySelector('.ticker');
  const footer = document.querySelector('.site-footer');
  if (ticker) ticker.insertAdjacentElement('afterend', community);
  else if (footer) footer.insertAdjacentElement('beforebegin', community);
  else document.body.append(community);

  const dialog = buildGuestbookDialog();
  document.body.append(dialog);
  bindGuestbook(dialog, guestbookButton);
  renderVisitorCount(count);
}

async function renderVisitorCount(target) {
  try {
    const result = await readVisitorCount();
    target.textContent = formatVisitorCount(result.count);
  } catch {
    target.textContent = '------';
  }
}

function buildGuestbookDialog() {
  const dialog = document.createElement('dialog');
  dialog.className = 'guestbook-dialog';
  dialog.setAttribute('aria-labelledby', 'guestbook-title');
  dialog.innerHTML = `
    <div class="dialog-toolbar">
      <strong>LITTLE PINK MEN GUESTBOOK</strong>
      <button class="icon-button" data-guestbook-close type="button" aria-label="Close guestbook">×</button>
    </div>
    <div class="guestbook-body">
      <h2 id="guestbook-title">Guestbook</h2>
      <p>Leave a note for Little Pink Men.</p>
      <form class="guestbook-form" data-guestbook-form>
        <label for="guestbook-name">Name</label>
        <input id="guestbook-name" name="name" type="text" autocomplete="name" maxlength="50" required>
        <label for="guestbook-message">Message</label>
        <textarea id="guestbook-message" name="message" rows="5" maxlength="500" required></textarea>
        <div class="signup-trap" aria-hidden="true">
          <label for="guestbook-company">Company</label>
          <input id="guestbook-company" name="company" type="text" tabindex="-1" autocomplete="off">
        </div>
        <input name="startedAt" type="hidden" data-guestbook-started-at>
        <p class="guestbook-status" data-guestbook-status role="status" aria-live="polite"></p>
        <button class="retro-button hot-button" type="submit">Sign guestbook</button>
      </form>
      <section class="guestbook-entries" aria-labelledby="guestbook-entries-title">
        <h3 id="guestbook-entries-title">Recent visitors</h3>
        <div data-guestbook-entries><p>Loading the guestbook…</p></div>
      </section>
    </div>`;
  return dialog;
}

function bindGuestbook(dialog, opener) {
  const closeButton = dialog.querySelector('[data-guestbook-close]');
  const form = dialog.querySelector('[data-guestbook-form]');
  const startedAt = dialog.querySelector('[data-guestbook-started-at]');
  const status = dialog.querySelector('[data-guestbook-status]');
  const entries = dialog.querySelector('[data-guestbook-entries]');
  const submit = form.querySelector('button[type="submit"]');

  opener.addEventListener('click', () => {
    startedAt.value = String(Date.now());
    status.textContent = '';
    if (!dialog.open) dialog.showModal();
    closeButton.focus();
    loadGuestbook(entries);
  });
  closeButton.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => opener.focus());

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    status.textContent = 'Signing the guestbook…';
    try {
      const response = await fetch(GUESTBOOK_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'The guestbook did not sign.');
      form.reset();
      startedAt.value = String(Date.now());
      status.textContent = 'Thanks for signing.';
      await loadGuestbook(entries);
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      submit.disabled = false;
    }
  });
}

async function loadGuestbook(target) {
  try {
    const response = await fetch(`${GUESTBOOK_ENDPOINT}?limit=50`, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'Guestbook unavailable.');
    renderGuestbookEntries(target, Array.isArray(result.entries) ? result.entries : []);
  } catch (error) {
    target.replaceChildren(paragraph(error instanceof Error ? error.message : String(error)));
  }
}

function renderGuestbookEntries(target, entries) {
  if (entries.length === 0) {
    target.replaceChildren(paragraph('Be the first to sign.'));
    return;
  }

  target.replaceChildren(...entries.map((entry) => {
    const article = document.createElement('article');
    article.className = 'guestbook-entry';
    const header = document.createElement('p');
    header.className = 'guestbook-entry-header';
    const name = document.createElement('strong');
    name.textContent = String(entry.name || 'Visitor');
    const date = document.createElement('time');
    date.dateTime = String(entry.timestamp || '');
    date.textContent = formatGuestbookDate(entry.timestamp);
    header.append(name, date);
    const message = paragraph(String(entry.message || ''));
    message.className = 'guestbook-entry-message';
    article.append(header, message);
    return article;
  }));
}

function formatGuestbookDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}

function paragraph(text) {
  const node = document.createElement('p');
  node.textContent = text;
  return node;
}
